/**
 * KiMap Editor - Coordinate System Documentation
 * ================================================
 * 
 * 2D Canvas Coordinate System:
 * - Origin: Top-left corner of the canvas
 * - X-axis: Points right (positive direction)
 * - Y-axis: Points down (positive direction)
 * - Unit: Pixels (logical coordinates, before zoom/pan)
 * 
 * 3D World Coordinate System:
 * - Origin: Center of the scene
 * - X-axis: Points right (positive direction)
 * - Y-axis: Points up (positive direction)
 * - Z-axis: Points forward/toward viewer (positive direction)
 * - Unit: Meters
 * 
 * Coordinate Mapping (2D → 3D):
 * - 2D X → 3D X (same direction)
 * - 2D Y → 3D Z (Y-down maps to Z-forward)
 * - Conversion factor: pixels ÷ 100 = meters
 * 
 * Calibration Scale:
 * - Stored in project.mapConfig.calibrationScale
 * - Unit: pixels per centimeter (px/cm)
 * - Used to convert real-world dimensions to canvas pixels
 * - Formula: realSize(mm) ÷ 10 × calibrationScale = canvasSize(px)
 * 
 * Model Dimensions:
 * - models.json stores real-world dimensions in millimeters
 * - Canvas dimensions calculated using calibration scale
 * - 3D dimensions calculated by dividing canvas pixels by 100
 */

export enum ToolType {
  SELECT = 'select',
  MOVE = 'move',
  ROTATE = 'rotate',
  WALL = 'wall',
  AREA = 'area',
  RECTANGLE = 'rectangle',
  CIRCLE = 'circle',
  POLYGON = 'polygon',
  DOOR = 'door',
  STAIRS = 'stairs',
  MEASURE = 'measure',
  TEXT = 'text',
  DIMENSION = 'dimension',
  MODEL_3D = 'model_3d',  // 3D模型放置工具
}

export type RightPanelTab = 'layers' | 'elements' | 'floors';

export interface Point2D {
  x: number;
  y: number;
}

export interface DragState {
  isDragging: boolean;
  elementId: string;
  pointIndex: number;
  startPoint: Point2D;
}

export interface CameraState {
  position: { x: number; y: number; z: number };
  target: { x: number; y: number; z: number };
  zoom: number;
}

export interface BaseElement {
  id: string;
  type: string;
  name: string;
  layerId: string;
  floorId: string;
  visible: boolean;
  locked: boolean;
  points: Point2D[];
  properties: Record<string, any>;
  rotation?: number;  // 旋转角度（度）- Y轴旋转（水平面内旋转）
  elevation?: number; // 离地高度（mm）- 元素底部距离地面的高度
}

export interface WallElement extends BaseElement {
  type: 'wall';
  thickness: number;
  height: number;
  material: string;
  color: string;
  opacity: number;      // 墙面透明度 0-1（默认1，完全不透明）
}

export interface AreaElement extends BaseElement {
  type: 'area';
  color: string;
  fillOpacity: number;
  areaName?: string;  // 区域名称
  showName?: boolean; // 是否显示名称
}

export interface ShapeElement extends BaseElement {
  type: 'rectangle' | 'circle' | 'polygon';
  height: number;
  color: string;
  rotation: number;
  opacity: number;        // 透明度 0-1（默认0.9）
  furnitureName?: string;  // 家具/设备名称
  width?: number;          // 家具宽度（mm）
  depth?: number;          // 家具深度（mm）
  radius?: number;         // 圆形半径（mm）
  passable?: boolean;      // 路径规划是否可穿行（默认false）
}

export interface DoorElement extends BaseElement {
  type: 'door';
  width: number;          // 门宽度（mm）
  height: number;         // 门高度（mm）
  openAngle: number;      // 开启角度
  wallId: string;         // 所在墙面ID
  wallSegmentIndex: number; // 墙面线段索引（从哪个点到下一个点）
  positionOnWall: number;   // 在墙面线段上的位置（0-1，0表示起点，1表示终点）
}

export interface StairElement extends BaseElement {
  type: 'stair';
  width: number;        // 楼梯宽度
  length: number;       // 楼梯长度
  height: number;       // 楼梯总高度
  steps: number;        // 台阶数量
  direction: 'up' | 'down';  // 上行或下行
}

export interface TextElement extends BaseElement {
  type: 'text';
  content: string;           // 文本内容
  fontSize: number;          // 字体大小（px，默认16）
  color: string;             // 文字颜色（默认#000000黑色）
  fontFamily: string;        // 字体（默认sans-serif）
  bold: boolean;             // 粗体（默认false）
  italic: boolean;           // 斜体（默认false）
  align: 'left' | 'center' | 'right';  // 对齐方式（默认center）
  borderColor: string;       // 边框颜色（默认#ADD8E6淡蓝色）
  backgroundColor: string;   // 背景颜色（默认#FFFFFF白色）
  backgroundOpacity: number; // 背景透明度 0-1（默认0.8）
  padding: number;           // 文本内边距（px，默认4）
}

export interface DimensionElement extends BaseElement {
  type: 'dimension';
  distance: number;     // 测量距离（mm）
  color: string;        // 标注颜色
  fontSize: number;     // 文字大小
  offset: number;       // 标注线偏移距离
}

export interface Model3DElement extends BaseElement {
  type: 'model3d';
  modelPath: string;      // 3D模型文件路径
  modelName: string;      // 模型名称
  modelCategory: string;  // 模型分类（文件夹名，如Sofa、Chair）
  width: number;          // Canvas width in pixels (calibrated)
  depth: number;          // Canvas depth in pixels (calibrated)
  height: number;         // Real height in mm
  realWidth: number;      // Real width in mm
  realDepth: number;      // Real depth in mm
  scale: number;          // 模型缩放比例
  color: string;          // 模型颜色
  isBuiltIn: boolean;     // 是否内置模型
  rotationX?: number;     // X轴旋转角度（度）- 俯仰角（前后倾斜）
  outlineData?: {         // 模型轮廓数据（缓存）
    lines: { x: number; y: number }[][];
    width: number;
    height: number;
  };
  passable?: boolean;     // 路径规划是否可穿行（默认false）
}

// 3D模型放置模式的状态
export interface Model3DPlacementState {
  modelPath: string;
  modelName: string;
  modelCategory: string;  // 模型分类（文件夹名）
  width: number;          // Canvas width in pixels (calibrated)
  depth: number;          // Canvas depth in pixels (calibrated)
  height: number;         // Real height in mm
  realWidth: number;      // Real width in mm
  realDepth: number;      // Real depth in mm
  isBuiltIn: boolean;
  thumbnail?: string;
  previewImage?: HTMLImageElement; // 预加载的缩略图
}

export type Element = WallElement | AreaElement | ShapeElement | DoorElement | StairElement | TextElement | DimensionElement | Model3DElement;

export interface Layer {
  id: string;
  name: string;
  visible: boolean;
  locked: boolean;
  color: string;
  elements: Element[];
}

export interface Floor {
  id: string;
  name: string;
  level: number;
  height: number;
  elevation: number;
  visible: boolean;
  isDefault?: boolean;  // 是否为默认楼层
  layers: Layer[];
}

export interface BaseMap {
  id: string;
  fileName: string;
  imageData: string;      // Base64 or URL
  width: number;          // 图片实际宽度（像素）
  height: number;         // 图片实际高度（像素）
  scale: number;          // 比例尺（像素/米）默认100
  offset: Point2D;        // 偏移位置
  rotation: number;       // 旋转角度
  opacity: number;        // 透明度 0-1
  visible: boolean;       // 是否显示
}

export interface MapConfig {
  center: Point2D;
  zoom: number;
  rotation: number;
  baseMap?: BaseMap;      // 底图配置
  validArea?: Point2D[];  // 有效绘制区域（多边形顶点）
  maxX?: number;          // 地图最大X坐标（mm）
  maxY?: number;          // 地图最大Y坐标（mm）
  // 校准数据
  calibrationScale?: number;  // 像素到厘米的比例（px/cm）
  realWidth?: number;         // 整张底图真实宽度（cm，含空白）
  realHeight?: number;        // 整张底图真实高度（cm，含空白）
  isCalibrated?: boolean;     // 是否已校准
  autoCalibrated?: boolean;   // 是否为自动校准（世界坐标直接是厘米）
  // 3D模型与真实世界的比例（3D模型1米 = 真实世界多少米）
  modelToRealScale?: number;  // 3D坐标到真实世界坐标的缩放比例
}

export interface Project {
  id: string;
  name: string;
  createdAt: string;
  updatedAt: string;
  mapConfig: MapConfig;
  floors: Floor[];
  metadata: Record<string, any>;
}

export interface EditorState {
  currentTool: ToolType;
  selectedElements: string[];
  currentFloor: string;
  currentLayer: string;
  project: Project | null;
  isDrawing: boolean;
  tempPoints: Point2D[];
  hoveredElement: string | null;
  clipboard: Element[];
  history: {
    past: Project[];
    future: Project[];
  };
  // 预览相关
  isPreviewOpen: boolean;
  previewCameraState: CameraState | null;
  // 墙面绘制属性
  wallProperties: {
    thickness: number;
    height: number;              // 3D渲染高度（逻辑上代表3000mm即3米）
    customAngle: number | null;  // 自定义角度（度）
    useCustomAngle: boolean;     // 是否使用自定义角度
    material: string;            // 墙面材质类型
    color: string;               // 墙面颜色
    opacity: number;             // 墙面透明度 0-1
  };
  // 右侧面板状态
  rightPanelTab: RightPanelTab;
  // 悬停的端点
  hoveredPoint: { elementId: string; pointIndex: number } | null;
  // 待命名的区域临时数据
  pendingArea: { points: Point2D[] } | null;
  // 待设置的家具临时数据
  pendingFurniture: { points: Point2D[] } | null;
  // 待设置的圆形临时数据
  pendingCircle: { points: Point2D[] } | null;
  // 待设置的多边形临时数据
  pendingPolygon: { points: Point2D[] } | null;
  // 待设置的门临时数据（两点确定位置区间）
  pendingDoor: { wallId: string; wallSegmentIndex: number; point1: Point2D; point2: Point2D } | null;
  // 门工具的绘制状态（记录第一个点和墙面信息）
  doorWindowDrawing: { wallId: string; wallSegmentIndex: number; startPoint: Point2D } | null;
  // 待设置的楼梯临时数据
  pendingStair: { points: Point2D[] } | null;
  // 待输入的文本临时数据
  pendingText: { point: Point2D } | null;
  // 地图校准状态
  isCalibrating: boolean;  // 是否正在校准
  calibrationPoint1: Point2D | null;  // 校准线第一个点
  calibrationPoint2: Point2D | null;  // 校准线第二个点（跟随鼠标）
  // 底图加载状态
  isBaseMapLoading: boolean;  // 是否正在加载底图
  // 3D模型放置模式
  model3DPlacement: Model3DPlacementState | null;
  // 隐藏的元素分类（仅用于编辑器显示，不影响导出）
  hiddenCategories: Record<string, boolean>;
  // 测量工具状态
  isMeasuring: boolean;
  currentMeasurement: Point2D[] | null;           // 当前正在绘制的测量点
  completedMeasurements: MeasurementLine[];       // 已完成的测量线
  measurementPreviewPoint: Point2D | null;        // 鼠标预览点
  // 外部API地址
  apiUrl: string | null;
  // 引导相关状态
  isGuidedTourEnabled: boolean;
  guidedTourStep: number;
  guidedTourCompleted: boolean;
  guidedTourDismissed: boolean;
  baseMapCentered: boolean;  // 底图是否已居中完成
}

// 测量工具相关类型
export interface MeasurementLine {
  id: string;                    // 唯一标识符
  points: Point2D[];             // 屏幕坐标点数组
  isComplete: boolean;           // 是否已完成
  totalLength: number;           // 总长度（厘米）
  segmentLengths: number[];      // 每段长度（厘米）
  createdAt: string;             // 创建时间
}

export interface KimapFile {
  version: string;
  type: 'kimap';
  project: Project;
  exportedAt: string;
  border: {
    minX: number;
    minY: number;
    minZ: number;
    maxX: number;
    maxY: number;
    maxZ: number;
  };
  threeData?: {
    geometry: any;
    materials: any;
  };
}
